import { createDraftSafeSelector } from '@reduxjs/toolkit'
import { shallowEqual } from 'react-redux'
import { RootState } from '../store'
import { OIAnalysisSettings, OpenInterestInitialStateType } from '../reducer/multiStrikeChart.reducer'
import { normalizeMultiStrikeColorPaletteId } from '../components/multiStikeChart/multiStrikeColorPalettes'
import { normalizeMultiStrikeChartType } from '../components/multiStikeChart/multistrikeChartTypeHelpers'
import { defaultScalesAndLinesSettings } from '../util/scalesAndLinesDefaults'
import { defaultStatusLineSettings } from '../util/statusLineDefaults'
import { ScalesAndLinesSettings, StatusLineSettings } from '../components/chartV2/chart.typings'

const state = (state: RootState) => state.multiStrikeChartStore as OpenInterestInitialStateType

type OiSettingsSlice = 'openInterestSettings' | 'oiChangeSettings'

const oiSettingsFieldSelectorCache = new Map<string, ReturnType<typeof createDraftSafeSelector>>()

const makeOiSettingsFieldSelector = <K extends keyof OIAnalysisSettings>(
    slice: OiSettingsSlice,
    field: K
) => {
    const cacheKey = `${slice}:${field}`
    if (!oiSettingsFieldSelectorCache.has(cacheKey)) {
        const useShallow =
            field === 'selectedExpiries' || field === 'expiriesMenuFromApi'
        const inputSelector = (s: RootState) => state(s)[slice][field]
        const combiner = (value: OIAnalysisSettings[K]) => value
        oiSettingsFieldSelectorCache.set(
            cacheKey,
            useShallow
                ? createDraftSafeSelector(inputSelector, combiner, {
                      memoizeOptions: { resultEqualityCheck: shallowEqual },
                  })
                : createDraftSafeSelector(inputSelector, combiner)
        )
    }
    return oiSettingsFieldSelectorCache.get(cacheKey)! as (s: RootState) => OIAnalysisSettings[K]
}

export const selectOpenInterestShowOI = makeOiSettingsFieldSelector('openInterestSettings', 'showOI')
export const selectOpenInterestReplayKey = makeOiSettingsFieldSelector('openInterestSettings', 'replayKey')
export const selectOpenInterestStrikeCount = makeOiSettingsFieldSelector('openInterestSettings', 'strikeCount')
export const selectOpenInterestSelectedExpiries = makeOiSettingsFieldSelector('openInterestSettings', 'selectedExpiries')
export const selectOpenInterestSecurityKey = makeOiSettingsFieldSelector('openInterestSettings', 'securityKey')
export const selectOpenInterestExpiriesMenuFromApi = makeOiSettingsFieldSelector('openInterestSettings', 'expiriesMenuFromApi')
export const selectOpenInterestExpiriesMenuLoading = makeOiSettingsFieldSelector('openInterestSettings', 'expiriesMenuLoading')
export const selectOpenInterestSelectedDate = makeOiSettingsFieldSelector('openInterestSettings', 'selectedDate')

export const selectOIChangeShowOI = makeOiSettingsFieldSelector('oiChangeSettings', 'showOI')
export const selectOIChangeReplayKey = makeOiSettingsFieldSelector('oiChangeSettings', 'replayKey')
export const selectOIChangeStrikeCount = makeOiSettingsFieldSelector('oiChangeSettings', 'strikeCount')
export const selectOIChangeSelectedExpiries = makeOiSettingsFieldSelector('oiChangeSettings', 'selectedExpiries')
export const selectOIChangeSecurityKey = makeOiSettingsFieldSelector('oiChangeSettings', 'securityKey')
export const selectOIChangeExpiriesMenuFromApi = makeOiSettingsFieldSelector('oiChangeSettings', 'expiriesMenuFromApi')
export const selectOIChangeExpiriesMenuLoading = makeOiSettingsFieldSelector('oiChangeSettings', 'expiriesMenuLoading')
export const selectOIChangeRangeType = makeOiSettingsFieldSelector('oiChangeSettings', 'rangeType')
export const selectOIChangeCustomFromDate = makeOiSettingsFieldSelector('oiChangeSettings', 'customFromDate')
export const selectOIChangeCustomToDate = makeOiSettingsFieldSelector('oiChangeSettings', 'customToDate')
export const selectOIChangeSelectedDate = makeOiSettingsFieldSelector('oiChangeSettings', 'selectedDate')

export const selectMultiStrikeChartTimebucket = createDraftSafeSelector(state, (store) => store.timeBucket)
export const selectMultiStrikeChartSecurities = createDraftSafeSelector(state, (store) => store.securities)
export const selectMultiStrikeChartType = createDraftSafeSelector(state, (store) =>
    normalizeMultiStrikeChartType(store.chartType, store.showChange)
)
export const selectMultiStrikeCumOi = createDraftSafeSelector(state, (store) => store.cumOi)
export const selectMultiStrikeShowChange = createDraftSafeSelector(state, (store) => store.showChange)
export const selectMultiStrikeChartStyles = createDraftSafeSelector(
    (s: RootState) => state(s).styles,
    (styles) => ({
        ...styles,
        colorPaletteId: normalizeMultiStrikeColorPaletteId(styles.colorPaletteId),
        customCallBaseColor: styles.customCallBaseColor ?? '#2F2FCD',
        customPutBaseColor: styles.customPutBaseColor ?? '#FFA719',
        customCallHueShift: styles.customCallHueShift ?? 50,
        customPutHueShift: styles.customPutHueShift ?? 50,
    }),
    { memoizeOptions: { resultEqualityCheck: shallowEqual } }
)
export const selectMultiStrikeChartCallPut = createDraftSafeSelector(state, (store) => store.callPut)
export const selectMultiStrikeNearAtm = createDraftSafeSelector(state, (store) => store.nearAtm)
export const selectMultiStrikeAtm = createDraftSafeSelector(state, (store) => store.atm)
export const selectMultiStrikeLoading = createDraftSafeSelector(state, (store) => store.loading)
export const selectMultiStrikeKey = createDraftSafeSelector(state, (store) => store.key)
export const selectMultiStrikeChartScalesAndLinesSettings = createDraftSafeSelector(
    state,
    (store) => ({
        ...defaultScalesAndLinesSettings,
        ...store.scalesAndLinesSettings,
    }) as ScalesAndLinesSettings,
    { memoizeOptions: { resultEqualityCheck: shallowEqual } }
)
export const selectMultiStrikeChartStatusLineSettings = createDraftSafeSelector(
    state,
    (store) => ({
        ...defaultStatusLineSettings,
        ...store.statusLineSettings,
    }) as StatusLineSettings,
    { memoizeOptions: { resultEqualityCheck: shallowEqual } }
)